'use client'

import { startTransition, useEffect, useState } from 'react'

// --- Store primitives (drop-in replacement for svelte/store) ---

export interface Readable<T> {
  subscribe(run: (value: T) => void): () => void
}

export interface Writable<T> extends Readable<T> {
  set(value: T): void
  update(updater: (value: T) => T): void
}

export function writable<T>(initial: T): Writable<T> {
  let value = initial
  const subscribers = new Set<(value: T) => void>()
  return {
    subscribe(run) {
      subscribers.add(run)
      run(value)
      return () => { subscribers.delete(run) }
    },
    set(newValue) {
      value = newValue
      subscribers.forEach((fn) => fn(value))
    },
    update(updater) {
      this.set(updater(value))
    },
  }
}

export function derived<T, S extends unknown[]>(
  stores: { [K in keyof S]: Readable<S[K]> },
  fn: (values: S) => T,
): Readable<T> {
  return {
    subscribe(run) {
      const values = stores.map(() => undefined) as unknown as S
      let pending = stores.length
      const unsubs = stores.map((store, i) =>
        store.subscribe((v) => {
          values[i] = v
          if (pending > 0) {
            pending--
          }
          if (pending === 0) {
            run(fn(values))
          }
        }),
      )
      return () => unsubs.forEach((u) => u())
    },
  }
}

export function get<T>(store: Readable<T>): T {
  let value!: T
  store.subscribe((v) => { value = v })()
  return value
}

// --- React hook ---

interface Options {
  /**
   * Whether or not to use `startTransition` to update the state
   *
   * Useful for Suspense compatibility.
   * Set to false when dealing with inputs, as otherwise it causes the input cursor position to reset when typing.
   */
  useTransition?: boolean
}

export function useStore<T>(
  store: Readable<T>,
  { useTransition = true }: Options = {},
): T {
  const [value, setValue] = useState<T>(() => get(store))
  useEffect(
    () =>
      store.subscribe((val) => {
        if (useTransition) {
          startTransition(() => {
            setValue(val)
          })
        } else {
          setValue(val)
        }
      }),
    [store, useTransition],
  )
  return value
}
